import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Briefcase, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "career-popup-dismissed";
const SHOW_DELAY_MS = 2500;

const CareerPopup = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Only on homepage
    if (location.pathname !== "/") {
      setIsVisible(false);
      return;
    }

    // Don't show again in this session if dismissed
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const tryShow = () => {
      const consent = localStorage.getItem("cookie-consent");
      if (!consent) return false;
      timer = setTimeout(() => {
        setIsMounted(true);
        // Trigger animation on next frame
        requestAnimationFrame(() => setIsVisible(true));
      }, SHOW_DELAY_MS);
      return true;
    };

    // Try immediately if consent already given
    if (tryShow()) {
      return () => {
        if (timer) clearTimeout(timer);
      };
    }

    // Otherwise watch for consent being granted
    const onStorage = (e: StorageEvent) => {
      if (e.key === "cookie-consent" && e.newValue) tryShow();
    };
    // CookieConsent component sets localStorage in same tab — storage event
    // doesn't fire then, so also poll briefly.
    const interval = setInterval(() => {
      if (localStorage.getItem("cookie-consent")) {
        clearInterval(interval);
        tryShow();
      }
    }, 1000);

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem(STORAGE_KEY, "1");
    setTimeout(() => setIsMounted(false), 300);
  };

  if (!isMounted) return null;

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 w-[calc(100vw-2rem)] max-w-sm",
        "transition-all duration-300 ease-out",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"
      )}
      role="dialog"
      aria-label="Vi søger en ny medarbejder"
    >
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label="Luk"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-3 pr-6">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Briefcase className="h-5 w-5" />
            </div>
            <h3 className="text-base font-bold text-foreground leading-snug">
              Vi leder efter en ny medarbejder – kunne det være dig?
            </h3>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Brænder du for uvildig skade- og forsikringsrådgivning? Se vores ledige stillinger og læs mere om hverdagen hos CECCA.
          </p>

          <div className="flex items-center gap-2">
            <Button asChild size="sm" className="flex-1">
              <Link to="/job-karriere" onClick={handleDismiss}>
                Læs mere
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-muted-foreground"
            >
              Ikke nu
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerPopup;
